import React from 'react';

// ============================================================
// JWFL Logo — futuristic neo-Tokyo crest
// Hexagonal shield, neon cyan + magenta gradients, glowing JW monogram,
// rising-sun motif behind it. Unique gradient IDs per instance.
// ============================================================
export const JwflLogo = ({ size = 48, showWordmark = false, className = '', wordmarkClass = '' }) => {
  const dim = size;
  const u = React.useMemo(() => Math.random().toString(36).slice(2, 9), []);
  const g = (k) => `${k}-${u}`;
  return (
    <div className={`flex items-center gap-2.5 ${className}`}>
      <svg
        width={dim}
        height={dim}
        viewBox="0 0 100 110"
        xmlns="http://www.w3.org/2000/svg"
        aria-label="JWFL"
        className="shrink-0 select-none drop-shadow-[0_0_24px_rgba(0,229,255,0.55)]"
      >
        <defs>
          <radialGradient id={g('body')} cx="0.5" cy="0.35" r="0.85">
            <stop offset="0%" stopColor="#0F1936" />
            <stop offset="55%" stopColor="#070A1A" />
            <stop offset="100%" stopColor="#02030A" />
          </radialGradient>
          <linearGradient id={g('rim')} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#00E5FF" />
            <stop offset="50%" stopColor="#7A00FF" />
            <stop offset="100%" stopColor="#FF006E" />
          </linearGradient>
          <linearGradient id={g('sun')} x1="0%" y1="100%" x2="0%" y2="0%">
            <stop offset="0%" stopColor="#FF006E" />
            <stop offset="60%" stopColor="#FF4D00" />
            <stop offset="100%" stopColor="#FFB400" stopOpacity="0.0" />
          </linearGradient>
          <linearGradient id={g('mono')} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#FFFFFF" />
            <stop offset="55%" stopColor="#A8FFFF" />
            <stop offset="100%" stopColor="#00E5FF" />
          </linearGradient>
          <radialGradient id={g('glow')} cx="0.5" cy="0.5" r="0.5">
            <stop offset="0%" stopColor="#00E5FF" stopOpacity="0.55" />
            <stop offset="100%" stopColor="#00E5FF" stopOpacity="0" />
          </radialGradient>
          <filter id={g('blur')} x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="1.2" />
          </filter>
        </defs>

        {/* outer glow */}
        <ellipse cx="50" cy="56" rx="50" ry="52" fill={`url(#${g('glow')})`} opacity="0.6" />

        {/* outer neon rim (hexagon-shield hybrid) */}
        <path
          d="M50 5 L86 16 L92 38 L82 78 L50 96 L18 78 L8 38 L14 16 Z"
          fill={`url(#${g('rim')})`}
        />
        {/* inner body */}
        <path
          d="M50 9 L82 19 L87 39 L78 74 L50 91 L22 74 L13 39 L18 19 Z"
          fill={`url(#${g('body')})`}
        />

        {/* rising sun rays (japan motif) */}
        <g opacity="0.55" filter={`url(#${g('blur')})`}>
          <path d="M50 55 L20 35 L24 30 Z" fill={`url(#${g('sun')})`} />
          <path d="M50 55 L28 25 L32 22 Z" fill={`url(#${g('sun')})`} />
          <path d="M50 55 L40 18 L44 17 Z" fill={`url(#${g('sun')})`} />
          <path d="M50 55 L52 14 L56 14 Z" fill={`url(#${g('sun')})`} />
          <path d="M50 55 L62 18 L66 21 Z" fill={`url(#${g('sun')})`} />
          <path d="M50 55 L72 22 L76 26 Z" fill={`url(#${g('sun')})`} />
          <path d="M50 55 L80 35 L78 40 Z" fill={`url(#${g('sun')})`} />
        </g>

        {/* sun disc */}
        <circle cx="50" cy="55" r="9" fill="#FF006E" filter={`url(#${g('blur')})`} opacity="0.85" />
        <circle cx="50" cy="55" r="6.5" fill="#FF3DA3" />

        {/* JW monogram */}
        <text
          x="50" y="42"
          textAnchor="middle"
          fontFamily="'Helvetica Neue', Arial Black, Impact, sans-serif"
          fontWeight="900"
          fontSize="20"
          fill={`url(#${g('mono')})`}
          letterSpacing="0.03em"
          style={{ filter: 'drop-shadow(0 0 6px rgba(0,229,255,0.85))' }}
        >
          JW
        </text>

        {/* tagline ribbon */}
        <g>
          <rect x="20" y="76" width="60" height="9" rx="1.5"
                fill="#04060F" stroke="#00E5FF" strokeWidth="0.6" opacity="0.95" />
          <text
            x="50" y="82.3"
            textAnchor="middle"
            fontFamily="'SF Mono', Menlo, monospace"
            fontWeight="700"
            fontSize="4.8"
            fill="#00E5FF"
            letterSpacing="0.34em"
            style={{ filter: 'drop-shadow(0 0 4px rgba(0,229,255,0.65))' }}
          >
            JWFL · EST 2026
          </text>
        </g>
      </svg>
      {showWordmark && (
        <div className="flex flex-col leading-none">
          <span className={`font-heading font-black tracking-wide text-white ${wordmarkClass || 'text-xl'}`}
                style={{ textShadow: '0 0 12px rgba(0,229,255,0.55)' }}>
            JWFL
          </span>
          <span className="text-[9px] uppercase tracking-[0.22em] text-[#00E5FF] mt-0.5 font-semibold">
            Japan World FL
          </span>
        </div>
      )}
    </div>
  );
};

// Compatibility alias — many files reference EwflLogo by name.
export const EwflLogo = JwflLogo;
export default JwflLogo;
